import type { Capability, Manifest } from './index.js';
import { outputSchemaFor } from './bindvars.js';
import { copyFor, fillCopy } from './copy.js';

/**
 * The two-surface seam.
 *
 * Everything upstream of this file is surface-agnostic. Everything downstream is
 * surface-specific. Keeping the fork this narrow is what makes "one manifest,
 * two surfaces" true rather than aspirational — and it means a spec change on
 * either side is a one-file edit.
 *
 * Spec facts encoded here, verified against primary sources 2026-08-27:
 *  - WebMCP lives at `document.modelContext` (moved from `navigator` in May 2026).
 *  - WebMCP ToolAnnotations is exactly { readOnlyHint, untrustedContentHint }.
 *  - WebMCP tool results are ANY JSON-serialisable value; the UA JSON-stringifies
 *    whatever execute() returns. There is no CallToolResult shape and no way to
 *    return UI — WebMCP's stated goal is that the *page* is the UI.
 *  - MCP Apps UI resources are PREDECLARED. The tool result carries no UI
 *    reference; the host resolves `_meta.ui.resourceUri` from the tool descriptor.
 *  - MCP Apps mimeType is `text/html;profile=mcp-app` (not text/html+skybridge,
 *    which is the legacy OpenAI Apps SDK spelling).
 *
 * The third surface, added 2026-09-08, is the OpenAI Apps SDK — what ChatGPT
 * renders a tool's widget through. It is MCP on the wire with its own metadata
 * vocabulary: a tool names its template in `_meta["openai/outputTemplate"]`,
 * the template is a resource with mimeType `text/html+skybridge`, and the
 * widget reads the tool result from `window.openai.toolOutput` (the served
 * widget already does, see `fromOpenAi()` in the server's fallback view). One
 * resource cannot carry two mimeTypes, so every view is published TWICE: the
 * MCP Apps resource, untouched, and an `.openai` twin with the same bytes.
 * The openai/* keys live only on the twin and in tool `_meta` — never inside
 * `_meta.ui`, which the MCP Apps spec defines field by field.
 */

export const MCP_APP_MIME = 'text/html;profile=mcp-app';
/** The Apps SDK spelling. ChatGPT looks for exactly this string. */
export const OPENAI_APP_MIME = 'text/html+skybridge';

/** Descriptor consumed by `document.modelContext.registerTool`. */
export interface WebMcpToolDescriptor {
  name: string;
  title: string;
  description: string;
  inputSchema: Record<string, unknown>;
  annotations: { readOnlyHint: boolean; untrustedContentHint: boolean };
}

/**
 * `cap.inputSchema` goes out verbatim and nothing else in the capability does.
 *
 * That is why `optionSets` — a parameter's values as decided by another
 * parameter — is modelled beside the schema rather than as `if`/`then` inside
 * it. Hosts validate a tool's schema against a subset of JSON Schema and drop
 * what they do not expect: claude.ai showed six tools of eight, and "a problem
 * displaying content", for two whose schemas merely carried `examples`. A
 * dependency is taught in prose, by a companion `list_*` tool and by the data
 * plane's refusal instead; none of those can cost a tool its place in the list.
 */
export function toWebMcpTool(cap: Capability): WebMcpToolDescriptor {
  return {
    name: cap.name,
    title: cap.title,
    description: cap.description,
    inputSchema: cap.inputSchema,
    // Only these two exist in the spec. Emitting destructiveHint/openWorldHint here
    // would be silently ignored, which is worse than not emitting them.
    annotations: {
      readOnlyHint: cap.annotations.readOnlyHint,
      untrustedContentHint: cap.annotations.untrustedContentHint,
    },
  };
}

export function webMcpToolsFor(m: Manifest): WebMcpToolDescriptor[] {
  return m.capabilities.filter((c) => c.surfaces.web.register).map(toWebMcpTool);
}

/**
 * What a WebMCP tool returns.
 *
 * Plain structured JSON, not `{content:[{type:'text'}]}`. The MCP block shape only
 * carries meaning through MCP-B's bridge; plain JSON is what Chrome's own docs
 * show and it stays forward-compatible with outputSchema.
 *
 * `summary` matters more than it looks: it is the sentence the agent reads aloud,
 * and it is the only part of the result most users ever perceive. `freshness` is
 * in the payload specifically so a stale answer gets narrated as stale.
 */
export interface WebMcpToolResult {
  ok: boolean;
  view: string;
  count: number;
  summary: string;
  /**
   * `origin` travels inside the envelope because a widget rendered inside
   * ChatGPT or Claude receives the payload and nothing else — no manifest, no
   * page context. Without it the freshness chip cannot name whose data this is,
   * and attribution is the one thing a third-party interface must never drop.
   */
  freshness: { state: 'live' | 'stale' | 'fixture'; asOf: string; origin?: string };
  items: unknown[];
  /** Where the user finishes the job — always on the operator's own site. */
  deepLink?: string;
  note?: string;
}

export function uiResourceUri(tenantId: string, view: string): string {
  return `ui://clawharbor/${tenantId}/${view}`;
}

/**
 * The Apps SDK twin of a view's resource: same view, same HTML, a different
 * mimeType. A suffix rather than a different scheme or path so that the twin
 * is recognisably the same view in a `resources/list` a human reads.
 */
export const OPENAI_RESOURCE_SUFFIX = '.openai';

export function openAiResourceUri(tenantId: string, view: string): string {
  return `${uiResourceUri(tenantId, view)}${OPENAI_RESOURCE_SUFFIX}`;
}

export function isOpenAiResourceUri(uri: string): boolean {
  return uri.endsWith(OPENAI_RESOURCE_SUFFIX);
}

/**
 * Resource `_meta` for the `.openai` twin, in the Apps SDK vocabulary.
 *
 *  - `openai/widgetCSP.connect_domains` says the same thing `_meta.ui.csp.
 *    connectDomains` says to an MCP Apps host: the widget's only network peer
 *    is the data plane on this server. `resource_domains` is empty because the
 *    widget inlines everything — no font, no image, no stylesheet — which is
 *    the third-party policy (nothing of theirs is rehosted) as much as a CSP.
 *  - `openai/widgetPrefersBorder` is false for the reason `prefersBorder` is
 *    omitted on the MCP Apps side: the widget paints its own frame.
 *  - `openai/widgetDescription` is what ChatGPT shows the model about the
 *    template; the same sentence as the resource description.
 */
export function openAiResourceMeta(opts: {
  publicOrigin: string;
  description: string;
}): Record<string, unknown> {
  return {
    'openai/widgetDescription': opts.description,
    'openai/widgetPrefersBorder': false,
    'openai/widgetCSP': { connect_domains: [opts.publicOrigin], resource_domains: [] },
  };
}

/** The tenant chrome a tool descriptor's Apps SDK status string is set in. */
export interface AppToolChrome {
  displayName: string;
  locale: string;
  copy?: Manifest['copy'];
}

export interface AppToolDescriptor {
  name: string;
  title: string;
  description: string;
  inputSchema: Record<string, unknown>;
  outputSchema?: Record<string, unknown>;
  annotations: { readOnlyHint: boolean };
  _meta: {
    /** MCP Apps: spec fields only. Nothing else may ever be added under this key. */
    ui: { resourceUri: string; visibility: string[] };
    /** OpenAI Apps SDK: the `.openai` twin of `ui.resourceUri`. */
    'openai/outputTemplate': string;
    /** Shown by ChatGPT while the call runs, in the tenant's language. */
    'openai/toolInvocation/invoking'?: string;
    /**
     * Whether the widget may call tools back through `window.openai.callTool`.
     * It may not: the widget talks to the data plane directly, so the honest
     * value is stated rather than left to a host default.
     */
    'openai/widgetAccessible': boolean;
  };
}

/**
 * The tool's description, plus the one thing the manifest's own text cannot say.
 *
 * A capability's `description` is written by the crawler against the FORM, and
 * whether that form may be RELAYED is a later decision an operator makes in the
 * dashboard. So the sentence about sending is appended here, where both dialects
 * pass through, rather than rewritten into the manifest every time a toggle
 * moves — and a model reading the tool then knows whether pressing Send in the
 * widget finishes the job or hands the person a link.
 *
 * The photo sentence is appended for every form tool, relayed or not: the values
 * arriving as arguments read off something the person shared is the flow this
 * whole surface exists for, and no crawler-written sentence can know it.
 */
function describeForWire(cap: Capability): string {
  if (!cap.form) return cap.description;
  const photo =
    ' If the person shared a photo or document with these values on it — a meter, an invoice, a ticket —' +
    ' read them from it and pass them as arguments; the widget shows them for the person to check first.';
  const sending = cap.form.relay
    ? " The widget's own Send button submits the form once the person confirms the values."
    : ' Nothing is submitted by this tool: the widget hands the person their filled-in form and the operator’s own page is where it is sent.';
  return `${cap.description}${photo}${sending}`;
}

export function toAppTool(
  cap: Capability,
  tenantId: string,
  chrome?: AppToolChrome,
): AppToolDescriptor {
  const d: AppToolDescriptor = {
    name: cap.name,
    title: cap.title,
    description: describeForWire(cap),
    inputSchema: cap.inputSchema,
    annotations: { readOnlyHint: cap.annotations.readOnlyHint },
    _meta: {
      ui: {
        resourceUri: uiResourceUri(tenantId, cap.view),
        visibility: cap.surfaces.app.visibility,
      },
      'openai/outputTemplate': openAiResourceUri(tenantId, cap.view),
      'openai/widgetAccessible': false,
    },
  };
  // `invoked` is deliberately not set: it is a static string, and the one thing
  // worth saying after a call — live or stale — is not known until the result
  // arrives. The widget's freshness chip says it; a fixed "Done" would not.
  if (chrome) {
    const asking = copyFor(chrome.locale, chrome.copy ?? {})['ui.asking'];
    if (asking) {
      d._meta['openai/toolInvocation/invoking'] = fillCopy(asking, { what: chrome.displayName });
    }
  }
  // Generated manifests carry no outputSchema, so without the synthesised fallback
  // `structuredContent` crosses the wire with no declared field list and the
  // description becomes the only contract — which is how a row field named
  // `price_2` came to read to an agent as a second price.
  const outputSchema = cap.outputSchema ?? outputSchemaFor(cap);
  if (outputSchema) d.outputSchema = outputSchema;
  return d;
}

export function appToolsFor(m: Manifest): AppToolDescriptor[] {
  const chrome: AppToolChrome = {
    displayName: m.identity.displayName,
    locale: m.identity.locale,
    copy: m.copy,
  };
  return m.capabilities
    .filter((c) => c.surfaces.app.register)
    .map((c) => toAppTool(c, m.id, chrome));
}

/** Distinct MCP Apps `ui://` resources this tenant must serve — one per referenced view. */
export function appResourcesFor(m: Manifest): Array<{ uri: string; view: string }> {
  const views = new Set(m.capabilities.filter((c) => c.surfaces.app.register).map((c) => c.view));
  return [...views].map((view) => ({ uri: uiResourceUri(m.id, view), view }));
}

/**
 * The `.openai` twins, one per MCP Apps resource and in the same order. A
 * separate list rather than extra entries in `appResourcesFor`, so that every
 * existing consumer of that list — the registry, the widget index, the
 * dashboard's edit checks — keeps meaning "the MCP Apps resources" without
 * learning a new field.
 */
export function openAiResourcesFor(m: Manifest): Array<{ uri: string; view: string }> {
  return appResourcesFor(m).map(({ view }) => ({ uri: openAiResourceUri(m.id, view), view }));
}

/**
 * MCP result envelope.
 *
 * `resultType` became REQUIRED on every result in core revision 2026-07-28. Hosts
 * still negotiating older revisions ignore the extra field, so emitting it
 * unconditionally is safe and omitting it is not.
 */
export function toMcpResult(payload: WebMcpToolResult, structured: unknown) {
  return {
    resultType: 'complete' as const,
    content: [{ type: 'text' as const, text: payload.summary }],
    structuredContent: structured,
  };
}
